import { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import AuthContext from '../context/AuthContext';
import axios from 'axios';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';

const Login = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [lastAttempt, setLastAttempt] = useState(0);
  const { email, password } = formData;
  const navigate = useNavigate();
  const { login, isAuthenticated, error, clearErrors } = useContext(AuthContext);

  useEffect(() => {
    // Check if user is already authenticated
    if (isAuthenticated) {
      navigate('/', { replace: true });
    }

    if (error) {
      toast.error(error);
      clearErrors();
    }
  }, [isAuthenticated, error, navigate, clearErrors]);

  const onChange = e => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const onSubmit = async e => {
    e.preventDefault();
    
    // Prevent too many requests - wait at least 2 seconds between attempts
    const now = Date.now();
    if (now - lastAttempt < 2000) {
      toast.error('Please wait a moment before trying again');
      return;
    }
    
    setIsSubmitting(true);
    setLastAttempt(now);
    
    try {
      // Use environment variable or fallback to development/production logic
      const baseURL = process.env.REACT_APP_API_URL || 
        (process.env.NODE_ENV === 'development' 
          ? 'http://localhost:5000/api' 
          : 'https://app-backends.vercel.app/api');
      const response = await axios.post(`${baseURL}/auth/login`, {
        email,
        password
      }, {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });
      
      if (response.data && response.data.token) {
        // Store token
        localStorage.setItem('token', response.data.token);
        
        // Set Authorization header for future requests
        axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;
        
        // Show success toast
        toast.success('Login successful!');
        
        // Wait for toast to complete before redirecting
        setTimeout(() => {
          window.location.href = '/';
        }, 2000);
      } else {
        toast.error('Invalid response from server');
        setIsSubmitting(false);
      }
    } catch (err) {
      console.error('Login error:', err);
      
      if (err.response) {
        if (err.response.status === 429) {
          // Too many requests
          toast.error('Too many login attempts. Please wait a moment and try again.');
        } else {
          // Show the specific error from the server
          const errorMessage = err.response.data?.error || 'Invalid credentials';
          toast.error(errorMessage);
        }
      } else {
        // Network error
        toast.error('Network error. Please check your connection.');
      }
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center py-4 px-4 sm:py-8 sm:px-6 lg:px-8" style={{ backgroundColor: '#1E605B' }}>
      <div className="w-full max-w-xs sm:max-w-sm">
        {/* Login Card */}
        <div className="bg-white rounded-lg shadow-xl p-4 sm:p-6">
          {/* Logo and Header */}
          <div className="text-center mb-4 sm:mb-6">
            {/* Logo Circle */}
            <div 
              className="mx-auto w-24 h-24 sm:w-28 sm:h-28 rounded-full flex items-center justify-center mb-4 sm:mb-6 shadow-lg overflow-hidden"
              style={{ backgroundColor: '#1E605B', borderRadius: '50%' }}
            >
              <img 
                src="/img/logo.png" 
                alt="Lekhan Mala Logo" 
                className="w-full h-full object-contain"
              />
            </div>
            
            {/* Title */}
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-1 sm:mb-2">Lekhan Mala Admin</h1>
            <p className="text-xs sm:text-sm text-gray-500">Sign in to your account</p>
          </div>
          
          {/* Login Form */}
          <form className="space-y-3 sm:space-y-4" onSubmit={onSubmit}>
            {/* Email Field */}
            <div>
              <label htmlFor="email" className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                Email *
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  className="block w-full pl-9 sm:pl-10 pr-3 py-2.5 sm:py-3 border border-gray-300 rounded-md placeholder-gray-400 text-gray-900 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm sm:text-sm"
                  placeholder="Enter your email"
                  value={email}
                  onChange={onChange}
                  disabled={isSubmitting}
                />
              </div>
            </div>

            {/* Password Field */}
            <div>
              <label htmlFor="password" className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                Password *
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  className="block w-full pl-9 sm:pl-10 pr-9 sm:pr-10 py-2.5 sm:py-3 border border-gray-300 rounded-md placeholder-gray-400 text-gray-900 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm sm:text-sm"
                  placeholder="Enter your password"
                  value={password}
                  onChange={onChange}
                  disabled={isSubmitting}
                />
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                  <button
                    type="button"
                    className="text-gray-400 hover:text-gray-600 focus:outline-none p-1"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeSlashIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                    ) : (
                      <EyeIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                    )}
                  </button>
                </div>
              </div>
            </div>

            {/* Sign In Button */}
            <div>
              <button
                type="submit"
                className={`w-full flex justify-center items-center py-2.5 sm:py-3 px-4 border border-transparent rounded-md text-sm font-medium text-white transition-colors duration-200 ${
                  isSubmitting 
                    ? 'bg-gray-400 cursor-not-allowed' 
                    : 'focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500'
                }`}
                style={!isSubmitting ? { backgroundColor: '#1E605B' } : {}}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Signing in...
                  </>
                ) : (
                  'Sign In'
                )}
              </button>
            </div>
          </form>
        </div>
        
        {/* Footer */}
        <div className="text-center mt-6 sm:mt-8">
          <p className="text-xs sm:text-sm text-white">© 2025 Lekhan Mala Admin Panel</p>
        </div>
      </div>
    </div>
  );
};

export default Login; 